/**
 * Signatures on a pressure-test job, on the technician's phone (PT-4,
 * `POST /pressure-testing/jobs/:id/signatures`): "Sign as technician" and
 * "Capture witness signature" — the witness signs on the tech's device.
 *
 * SENT AS A PNG DATA URL, with the signer's name. A re-sign replaces. Allowed
 * on any job that is not cancelled, until its certificate is issued; after
 * that the API answers 409 `certificate_issued`, and this says so plainly
 * instead of offering a pad the API will refuse.
 *
 * WHAT IS ON FILE IS KNOWN ONLY THROUGH THE CERTIFICATE. There is no read of a
 * job's signatures except `GET /certificates/:id`, which exists once the job's
 * certificate is drafted. Before that, this cannot say whether either signature
 * is on file — and it does not guess "missing": it says it cannot show them,
 * and after a signature is saved it shows what the API answered.
 *
 * ONLINE ONLY. The offline queue has no signature write; a signature captured
 * in a dead zone and "saved" to nowhere is the failure this vertical's field
 * flow refuses everywhere (see PtChartCapture).
 */
import React, { useState } from 'react';
import { CheckCircle2, Loader2, PenLine } from 'lucide-react';
import { toast } from 'sonner';
import SignaturePad from '../../../components/SignaturePad';
import { useAuth } from '../../../contexts/AuthContext';
import { usePtCertificate, usePtCertificates, useSignPtJob } from '../../../hooks/usePressureTesting';
import { formatInstant } from '../../../lib/calendar-date';
import { queryLoadFailure } from '../../../lib/load-failure';
import { useSyncStore } from '../../../lib/offline/sync-store';
import type { PtJobDetail, PtSignature, PtSignatureRole, PtSignatures } from '../../../types/pressure-testing';
import { PT_INPUT } from '../PtBits';
import { ptWriteError } from '../PtJobDialogs';
import { PT_FIELD_PRIMARY, PT_FIELD_SECONDARY, PtFieldCard, PtFieldSectionTitle, PtRefusal } from './PtFieldBits';

const ROLE_TITLE: Record<PtSignatureRole, string> = { technician: 'Technician', witness: 'Witness' };

function SignatureForm({
  jobId,
  role,
  defaultName,
  onDone,
  onCancel,
  onIssued,
}: {
  jobId: string;
  role: PtSignatureRole;
  defaultName: string;
  onDone: (s: PtSignatures | null) => void;
  onCancel: () => void;
  onIssued: () => void;
}) {
  const sign = useSignPtJob();
  const [name, setName] = useState(defaultName);
  const [data, setData] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = () => {
    if (!name.trim()) return setError(role === 'witness' ? "Enter the witness's name." : 'Enter your name.');
    if (!data) return setError('Draw the signature and tap Confirm Signature.');
    setError(null);
    sign.mutate(
      { jobId, body: { role, signer_name: name.trim(), signature_data: data } },
      {
        onSuccess: (res) => {
          toast.success(role === 'witness' ? 'Witness signature saved' : 'Your signature is saved');
          onDone(res.data);
        },
        onError: (err) => {
          const e = ptWriteError(err, 'The signature was not saved. Try again.');
          if (e.code === 'certificate_issued') onIssued();
          else setError(e.message);
        },
      },
    );
  };

  return (
    <div className="mt-2" data-testid={`pt-field-sign-${role}-form`}>
      <PtFieldCard className="space-y-3 px-4 py-4">
        <label className="block">
          <span className="mb-1 block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
            {role === 'witness' ? "Witness's name" : 'Your name'}
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            autoComplete="name"
            className={`${PT_INPUT} h-12 text-base`}
            data-testid={`pt-field-sign-${role}-name`}
          />
        </label>
        <SignaturePad label={role === 'witness' ? 'Witness signature' : 'Technician signature'} onSave={setData} onClear={() => setData(null)} />
        <PtRefusal message={error} testId={`pt-field-sign-${role}-error`} />
        <div className="grid grid-cols-2 gap-2">
          <button type="button" className={PT_FIELD_SECONDARY} onClick={onCancel} disabled={sign.isPending}>
            Cancel
          </button>
          <button type="button" className={PT_FIELD_PRIMARY} onClick={save} disabled={sign.isPending} data-testid={`pt-field-sign-${role}-save`}>
            {sign.isPending ? <Loader2 className="size-5 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="size-5" aria-hidden="true" />}
            Save
          </button>
        </div>
      </PtFieldCard>
    </div>
  );
}

function OnFile({ role, signature, known, tz }: { role: PtSignatureRole; signature: PtSignature | null | undefined; known: boolean; tz: string | null }) {
  return (
    <p className="text-sm text-fieldiq-text" data-testid={`pt-field-signature-${role}`} data-present={known ? String(!!signature) : 'unknown'}>
      <span className="font-bold text-white">{ROLE_TITLE[role]}:</span>{' '}
      {!known
        ? '—'
        : signature
          ? `${signature.signer_name}, ${formatInstant(signature.signed_at, tz) ?? signature.signed_at_local ?? '—'}`
          : 'not signed'}
    </p>
  );
}

export function PtFieldSignatures({ job, timeZone }: { job: PtJobDetail; timeZone: string | null }) {
  const { user } = useAuth();
  const isOnline = useSyncStore((s) => s.isOnline);
  const certs = usePtCertificates();
  const cert = (certs.data?.data ?? []).find((c) => c.job_id === job.id) ?? null;
  const detail = usePtCertificate(cert?.id);
  const [open, setOpen] = useState<PtSignatureRole | null>(null);
  const [saved, setSaved] = useState<PtSignatures | null>(null);
  const [issuedRefusal, setIssuedRefusal] = useState(false);

  if (job.status === 'cancelled') return null;

  const issued = cert?.status === 'issued' || issuedRefusal;
  // The freshest answer: what the last save returned, else the certificate's read.
  const signatures: PtSignatures | null = saved ?? detail.data?.data?.signatures ?? null;
  const known = !!signatures;
  // /auth/me answers snake_case; the User type says camelCase. Read both (ProfilePage does).
  const u = user as { first_name?: string; last_name?: string; firstName?: string; lastName?: string } | null;
  const ownName = [u?.first_name || u?.firstName, u?.last_name || u?.lastName].filter(Boolean).join(' ');

  return (
    <section className="mt-6" data-testid="pt-field-signatures">
      <PtFieldSectionTitle>Signatures</PtFieldSectionTitle>
      <PtFieldCard className="space-y-1 px-4 py-3">
        <OnFile role="technician" signature={signatures?.technician} known={known} tz={timeZone} />
        <OnFile role="witness" signature={signatures?.witness} known={known} tz={timeZone} />
        {!known && !certs.isLoading && !detail.isLoading && (
          <p className="pt-1 text-xs text-fieldiq-muted" data-testid="pt-field-signatures-unknown">
            {queryLoadFailure(certs) || queryLoadFailure(detail)
              ? 'The signatures on file could not be loaded.'
              : 'Signatures already on this job show here once the office drafts its certificate. Signing again replaces one.'}
          </p>
        )}
      </PtFieldCard>

      {issued ? (
        <p
          role="status"
          data-testid="pt-field-signatures-issued"
          className="mt-2 rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-green bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text"
        >
          This job's certificate{cert?.certificate_number ? ` ${cert.certificate_number}` : ''} has been issued, so its signatures can no longer change.
        </p>
      ) : !isOnline ? (
        <p
          role="status"
          data-testid="pt-field-signatures-offline"
          className="mt-2 rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text"
        >
          Needs signal to save a signature. It is not saved for later — sign when you are back in coverage.
        </p>
      ) : open ? (
        <SignatureForm
          key={open}
          jobId={job.id}
          role={open}
          defaultName={open === 'technician' ? signatures?.technician?.signer_name || ownName : signatures?.witness?.signer_name || ''}
          onCancel={() => setOpen(null)}
          onDone={(s) => {
            if (s) setSaved(s);
            setOpen(null);
          }}
          onIssued={() => {
            setIssuedRefusal(true);
            setOpen(null);
          }}
        />
      ) : (
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <button type="button" className={PT_FIELD_SECONDARY} onClick={() => setOpen('technician')} data-testid="pt-field-sign-technician">
            <PenLine className="size-5" aria-hidden="true" />
            {signatures?.technician ? 'Re-sign as technician' : 'Sign as technician'}
          </button>
          <button type="button" className={PT_FIELD_SECONDARY} onClick={() => setOpen('witness')} data-testid="pt-field-sign-witness">
            <PenLine className="size-5" aria-hidden="true" />
            {signatures?.witness ? 'Re-capture witness signature' : 'Capture witness signature'}
          </button>
        </div>
      )}
    </section>
  );
}
